import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { colorsTheme } from 'resources/theme/styled/colors';
import { SubmitButtonsFormValues } from '../SubmitButtons';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { accountStepValidationSchema } from 'validations/intake-form/intake-form-steps';
import {
  AmountOptions,
  NumberOfTransactionsOptions,
  PurposeOfBankAccountOptions,
  TotalTransactionsPerMonthOptions,
} from 'enums/onboarding/crm';

// components
import InnerForm from './InnerForm';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import SectionTitleNavigateButton from '../SectionTitleNavigateButton';
import { AutocompleteOption, Form, SectionIntro } from '@ui';

export interface FormValuesModel extends SubmitButtonsFormValues {
  purpose: PurposeOfBankAccountOptions[];
  transactionCountries: string[];
  transactionCountriesInitialOptions?: AutocompleteOption[];
  numberOfIncomingTransactions: NumberOfTransactionsOptions | null;
  valueOfIncomingTransactions: TotalTransactionsPerMonthOptions | null;
  numberOfOutgoingTransactions: NumberOfTransactionsOptions | null;
  valueOfOutgoingTransactions: TotalTransactionsPerMonthOptions | null;
  monthlyBalanceAmount: AmountOptions | null;
  accountInflowsDetails: string;
  accountOutflowsDetails: string;
}

interface IProps {
  disabled?: boolean;
  renderNavigateButtonInTitle?: boolean;
}

const Account = ({ disabled, renderNavigateButtonInTitle }: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const {
    intakeForm,
    navigateToNextStep,
    updateIntakeFormFields,
    navigateToPreviousStep,
  } = useEntityIntakeFormContext();

  const initialValues = useMemo<FormValuesModel | null>(() => {
    if (!intakeForm) {
      return null;
    }

    const intakeFormFields = intakeForm.fields;

    let transactionCountriesInitialOptions: AutocompleteOption[] | undefined =
      undefined;
    if (
      intakeFormFields?.accountCountries &&
      intakeFormFields.accountCountries.length
    ) {
      transactionCountriesInitialOptions =
        intakeFormFields.accountCountries.map((e) => ({
          id: e,
          label: intakeForm.metadata.countries[e],
        }));
    }

    return {
      submitType: null,
      purpose: intakeFormFields.purposeOfAccount || [],
      transactionCountries: intakeFormFields.accountCountries || [],
      transactionCountriesInitialOptions,
      numberOfIncomingTransactions:
        intakeFormFields.incomingTransactionNumber || null,
      valueOfIncomingTransactions:
        intakeFormFields.incomingTransactionVolume || null,
      numberOfOutgoingTransactions:
        intakeFormFields.outgoingTransactionNumber || null,
      valueOfOutgoingTransactions:
        intakeFormFields.outgoingTransactionVolume || null,
      monthlyBalanceAmount: intakeFormFields.avarageMonthlyBalance || null,
      accountInflowsDetails: intakeFormFields.accountInflowsDetails || '',
      accountOutflowsDetails: intakeFormFields.accountOutflowsDetails || '',
    };
  }, [intakeForm]);

  const handleSubmit = async (values: FormValuesModel) => {
    if (!intakeForm) {
      return;
    }
    await updateIntakeFormFields(
      {
        purposeOfAccount: values.purpose.length ? values.purpose : null,
        accountCountries: values.transactionCountries.length
          ? values.transactionCountries
          : null,
        incomingTransactionNumber: values.numberOfIncomingTransactions || null,
        incomingTransactionVolume: values.valueOfIncomingTransactions || null,
        outgoingTransactionNumber: values.numberOfOutgoingTransactions || null,
        outgoingTransactionVolume: values.valueOfOutgoingTransactions || null,
        avarageMonthlyBalance: values.monthlyBalanceAmount || null,
        accountInflowsDetails: values.accountInflowsDetails || null,
        accountOutflowsDetails: values.accountOutflowsDetails || null,
      },
      undefined,
    );
    switch (values.submitType) {
      case 'save-continue':
        navigateToNextStep();
        break;
      case 'back':
        navigateToPreviousStep();
        break;
    }
  };

  return (
    <>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant={disabled ? 'h5' : undefined}
        title={
          <DivAlignCenter>
            {t('intake_form.account.section_title')}
            {renderNavigateButtonInTitle ? (
              <SectionTitleNavigateButton navigateToStepKey="account" />
            ) : null}
          </DivAlignCenter>
        }
      />
      <Form
        disabled={disabled}
        onSubmit={handleSubmit}
        renderForm={<InnerForm />}
        initialValues={initialValues}
        validationSchema={accountStepValidationSchema}
      />
    </>
  );
};

export default Account;
